import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AuthShell, ErrorNote } from "./Login";

const inputClass =
  "w-full rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-4 py-3.5 text-base font-body text-[#1F2937] outline-none transition-colors focus:border-[#0D9488]";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthShell
        title="Check your inbox"
        subtitle="If an account exists for that email, we've sent a reset link."
        footer={<Link to="/login" className="font-bold text-[#0D9488] hover:underline" data-testid="back-to-login">Back to log in</Link>}
      >
        <div className="rounded-2xl border-2 border-[#10B981]/40 bg-[#10B981]/10 px-4 py-4 text-sm font-semibold text-[#047857]" data-testid="forgot-sent-message">
          <div className="flex items-center gap-2 font-heading text-base font-bold"><CheckCircle2 size={18} /> Reset link on its way</div>
          <p className="mt-1 font-body text-sm text-[#047857]/90">Tap the button in the email to pick a new password. The link expires in <strong>60 minutes</strong>. If it doesn't arrive, check spam or try again.</p>
        </div>
        <button
          type="button"
          onClick={() => { setSent(false); setEmail(""); }}
          data-testid="forgot-send-again"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-[#E5DECF] bg-white px-6 py-3 text-base font-bold text-[#1F2937] hover:border-[#0D9488]"
        >
          Send to another email
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot your password?"
      subtitle="Enter your account email and we'll send you a link to pick a new one."
      footer={<Link to="/login" className="font-bold text-[#0D9488] hover:underline" data-testid="back-to-login">Back to log in</Link>}
    >
      <form onSubmit={submit}>
        <ErrorNote message={error} />
        <label className="block text-sm font-bold text-[#1F2937] mb-2">Email</label>
        <div className="relative">
          <Mail size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            data-testid="forgot-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${inputClass} pl-11`}
            placeholder="you@school.com"
            autoComplete="email"
          />
        </div>
        <button type="submit" disabled={busy || !email.trim()} data-testid="forgot-submit-button"
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-lg font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5 disabled:opacity-50">
          {busy ? "Sending..." : "Send reset link"} <ArrowRight size={20} />
        </button>
        <p className="mt-4 font-body text-xs text-[#6B7280]">Students should ask a parent or teacher to reset their password — student accounts sign in with a username instead.</p>
      </form>
    </AuthShell>
  );
}
